/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import noop from 'lodash/noop';
import { useRouter } from 'next/navigation';
import {
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type MouseEventHandler,
  useRef,
  useState,
} from 'react';
import { useFocusWithin } from 'react-aria';
import type { MessageKey } from 'types/LocaleMessageKey';

import BackdropBlur from '@/app/components/BackdropBlur';
import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import type { MotionProps } from '@/app/components/Motion';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import LandingPageHeroChicletCtaButton from './LandingPageHeroChicletCtaButton';
import LandingPageHeroChicletCtaLink, {
  type LandingPageHeroChicletCtaLinkProps,
} from './LandingPageHeroChicletCtaLink';
import LandingPageHeroChicletModal, {
  type LandingPageHeroChicletModalProps,
} from './LandingPageHeroChicletModal';
import type { LandingPageHeroChicletCtaProps } from './types';

export interface LandingPageHeroChicletProps
  extends ComponentPropsWithoutRef<'div'> {
  readonly cta?: LandingPageHeroChicletCtaLinkProps | undefined;
  readonly image: Pick<
    ImageProps,
    'alt' | 'format' | 'localizedAlt' | 'localizedSrc' | 'src'
  >;
  readonly modal?:
    | Omit<LandingPageHeroChicletModalProps, 'isOpen' | 'onToggle'>
    | undefined;
  readonly modalCta?: LandingPageHeroChicletCtaProps;
  readonly motionProps?: MotionProps;
  readonly onFocusWithin?: (e: FocusEvent) => void;
  readonly title: MessageKey;
}

const LandingPageHeroChiclet = ({
  cta,
  image,
  modal,
  modalCta,
  motionProps = {},
  onClick,
  onFocusWithin = noop,
  title,
  ...props
}: LandingPageHeroChicletProps) => {
  const router = useRouter();
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const modalButtonRef = useRef<HTMLButtonElement>(null);
  const _motionProps: MotionProps = {
    ...motionProps,
    animate: { opacity: 1 },
    initial: { opacity: 0 },
    transition: {
      duration: 1,
      ...motionProps.transition,
      type: 'tween',
    },
  };
  const handleWrapperClick: MouseEventHandler<HTMLDivElement> = (e) => {
    onClick?.(e);
    if (modal) {
      modalButtonRef.current?.click();
    }
    if (cta) {
      router.push(cta.href);
    }
  };
  const handleModalButtonClick = () => {
    modalCta?.onClick?.();
    setIsModalOpen(true);
  };
  const handleModalToggle = (isOpen: boolean) => {
    if (!isOpen) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div
        data-cy="landing-page-hero-chiclet"
        {...focusWithinProps}
        {...props}
        className={cx(
          'mb-6 inline-block w-full max-w-[360px] cursor-pointer max-md:mx-auto',
          props.className,
        )}
        onClick={handleWrapperClick}
      >
        <div className="relative z-10 p-1">
          <BackdropBlur
            {..._motionProps}
            color={{ sm: 'dark/40', lg: 'dark/40' }}
            variant="chiclet"
          />
          <Motion className="relative flex" {..._motionProps}>
            <div className="relative aspect-3/2 w-44 overflow-hidden rounded-xl px-20">
              <Image className="object-cover" fill sizes="200px" {...image} />
            </div>
            <div className="flex flex-col justify-center px-4 pt-3 pb-4 lg:pr-7 lg:pl-6">
              <Typography
                className="mb-3 leading-snug"
                color="light"
                variant="body-small"
              >
                <RichText messageKey={title} />
              </Typography>
              {cta && <LandingPageHeroChicletCtaLink {...cta} />}
              {modalCta && (
                <LandingPageHeroChicletCtaButton
                  {...modalCta}
                  onClick={handleModalButtonClick}
                  ref={modalButtonRef}
                />
              )}
            </div>
          </Motion>
        </div>
      </div>
      {modal && (
        <LandingPageHeroChicletModal
          {...modal}
          isOpen={isModalOpen}
          onToggle={handleModalToggle}
        />
      )}
    </>
  );
};

export default LandingPageHeroChiclet;
